import React, { memo, useMemo } from 'react';
import { useGame } from '../../context/GameContext';
import './DictionaryBoard.css';

// SVG icons - memoized to prevent unnecessary re-renders
const EyeOpenIcon = memo(() => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
  </svg>
));

const EyeClosedIcon = memo(() => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
    <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
    <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
  </svg>
));

// Dictionary word item component - memoized to prevent unnecessary re-renders
const DictionaryItem = memo(({ entry, isVisible, onToggleVisibility, language }) => {
  const displayWord = language === 'pt-br' ? entry.word : entry.spelling;
  
  return (
    <div className="dictionary-item">
      <div className="word-text">{displayWord}</div>
      <button 
        className="visibility-toggle" 
        onClick={() => onToggleVisibility(entry.word)}
        aria-label={isVisible ? "Hide translation" : "Show translation"}
      >
        {isVisible ? <EyeClosedIcon /> : <EyeOpenIcon />}
      </button>
      {isVisible && (
        <div className="word-translation">
          {language === 'pt-br' ? entry.spelling : entry.word}
        </div>
      )}
    </div>
  );
});

const DictionaryBoard = () => {
  const { 
    dictionary, 
    dictionaryLanguage, 
    setDictionaryLanguage, 
    wordVisibility, 
    toggleWordVisibility 
  } = useGame();

  // Memoize the sorted dictionary to prevent unnecessary re-sorting
  const sortedDictionary = useMemo(() => {
    if (!dictionary || !Array.isArray(dictionary)) return [];
    
    // Sort by the current language
    return [...dictionary].sort((a, b) => {
      const aText = dictionaryLanguage === 'pt-br' ? a.word : a.spelling;
      const bText = dictionaryLanguage === 'pt-br' ? b.word : b.spelling;
      return aText.localeCompare(bText);
    });
  }, [dictionary, dictionaryLanguage]);

  return (
    <div className="dictionary-board">
      <div className="dictionary-header">
        <h3>Dictionary</h3>
        <div className="language-toggle">
          <button 
            className={dictionaryLanguage === 'pt-br' ? 'active' : ''}
            onClick={() => setDictionaryLanguage('pt-br')}
          >
            Português
          </button>
          <button 
            className={dictionaryLanguage === 'gibberers' ? 'active' : ''}
            onClick={() => setDictionaryLanguage('gibberers')}
          >
            Gibberers
          </button>
        </div>
      </div>
      
      <div className="dictionary-content">
        {sortedDictionary.length > 0 ? (
          <div className="dictionary-words">
            {sortedDictionary.map(entry => (
              <DictionaryItem 
                key={`${entry.word}-${entry.spelling}`}
                entry={entry}
                isVisible={wordVisibility[entry.word]}
                onToggleVisibility={toggleWordVisibility}
                language={dictionaryLanguage}
              />
            ))}
          </div>
        ) : (
          <div className="empty-dictionary">
            <p>No words in the dictionary yet.</p>
            <p>Create words to build your language!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(DictionaryBoard); 